/**
 * Users Migration Table Component
 *
 * Displays migration users in a table format with badges for migration status.
 * Supports row actions for edit and delete.
 */

import { useState } from 'react';
import { format } from 'date-fns';
import { Edit, Trash2, AlertCircle, Loader2, CheckCircle, Clock, Copy, Check } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { MigrationUser } from '@/types/user-migration';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

/**
 * Users Migration Table Props
 */
interface UsersMigrationTableProps {
  /** Array of migration users */
  users: MigrationUser[];
  /** Loading state */
  isLoading?: boolean;
  /** Error state */
  error?: Error | null;
  /** Retry callback for errors */
  onRetry?: () => void;
  /** Edit callback */
  onEdit?: (user: MigrationUser) => void;
  /** Delete callback */
  onDelete?: (user: MigrationUser) => void;
}

/**
 * Format Date Time
 * Formats ISO date string to readable format
 */
function formatDateTime(dateString: string | null): string {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm:ss');
  } catch {
    return dateString;
  }
}

/**
 * Truncate Text
 * Truncates long text with ellipsis
 */
function truncate(text: string | null | undefined, maxLength = 30): string {
  if (!text) return '-';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

/**
 * Copy ID Button
 * Inline button to copy a user ID to clipboard with visual feedback
 */
function CopyIdButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast({
        title: 'Copied',
        description: 'User ID copied to clipboard',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may not be available
    }
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-5 w-5 p-0 ml-1 shrink-0"
      onClick={handleCopy}
      aria-label="Copy user ID"
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
    </Button>
  );
}

/**
 * Loading State
 */
function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 space-y-2">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      <p className="text-sm text-muted-foreground">Loading migration users...</p>
    </div>
  );
}

/**
 * Empty State
 */
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 space-y-2">
      <AlertCircle className="h-12 w-12 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">No migration users found</p>
      <p className="text-xs text-muted-foreground">Try adjusting your search or filters</p>
    </div>
  );
}

/**
 * Users Migration Table Component
 */
export function UsersMigrationTable({
  users,
  isLoading = false,
  error = null,
  onRetry,
  onEdit,
  onDelete,
}: UsersMigrationTableProps) {
  // Error State
  if (error && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <div className="text-center">
          <h3 className="text-lg font-semibold">Failed to load migration users</h3>
          <p className="text-sm text-muted-foreground mt-1">{error.message}</p>
        </div>
        {onRetry && (
          <Button variant="outline" onClick={onRetry}>
            Try Again
          </Button>
        )}
      </div>
    );
  }

  // Loading State
  if (isLoading) {
    return <LoadingState />;
  }

  // Empty State
  if (users.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Email</TableHead>
            <TableHead>First Name</TableHead>
            <TableHead>Last Name</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Created At</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map(user => (
            <TableRow key={user.cognitoId} className="group">
              <TableCell className="font-medium">
                <div className="flex flex-col">
                  <span className="text-sm">{truncate(user.email, 40)}</span>
                  <div className="flex items-center">
                    <span className="text-xs text-muted-foreground font-mono break-all">
                      {user.cognitoId || '-'}
                    </span>
                    {user.cognitoId && <CopyIdButton value={user.cognitoId} />}
                  </div>
                </div>
              </TableCell>
              <TableCell>{user.firstName || '-'}</TableCell>
              <TableCell>{user.lastName || '-'}</TableCell>
              <TableCell>{user.phone || '-'}</TableCell>
              <TableCell>
                <Badge
                  variant={user.isMigrated ? 'default' : 'secondary'}
                  className={cn(
                    'gap-1',
                    user.isMigrated
                      ? 'bg-green-100 text-green-800 hover:bg-green-200'
                      : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                  )}
                >
                  {user.isMigrated ? (
                    <>
                      <CheckCircle className="h-3 w-3" />
                      Migrated
                    </>
                  ) : (
                    <>
                      <Clock className="h-3 w-3" />
                      Awaiting
                    </>
                  )}
                </Badge>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatDateTime(user.createdAt)}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  {onEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(user)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit className="h-4 w-4" />
                      <span className="sr-only">Edit user</span>
                    </Button>
                  )}
                  {onDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(user)}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">Delete user</span>
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
